/**
 * Shared analytics helpers for the YC application funnel.
 */
export type YcAppSurface = "public" | "canvas";

export type YcAppElement =
  | "page"
  | "banner"
  | "block"
  | "editor"
  | "results"
  | "suggestion"
  | "vision_scale"
  | "one_liner";

export type YcAppEntryPoint =
  | "landing"
  | "direct"
  | "claim_link"
  | "canvas_banner"
  | "canvas_block"
  | "canvas_row"
  | "labs_panel"
  | "levai"
  // The signup gate (LEV-2451): a signed-out visitor's canvas Start was
  // gated, so yc_app_started never fired upstream; this is the entry point
  // once they land here post-claim and the panel auto-drafts.
  | "signup_gate"
  | "program_handoff";

export type YcAppCalculationType = "initial" | "recalculate";

export const YC_APP_EVENT_FAMILY = "yc_application";

export function getEmailDomain(email: string | null | undefined): string | null {
  const domain = email?.split("@")[1]?.trim().toLowerCase();
  return domain || null;
}

export async function hashClaimTokenForAnalytics(
  claimToken: string | null | undefined,
): Promise<string | null> {
  if (!claimToken) return null;
  const data = new TextEncoder().encode(claimToken);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 8);
}

export function buildYcAppCommonProps(args: {
  claimTokenHash?: string | null;
  isLoggedIn?: boolean;
  projectId?: string | null;
  ycAppElement?: YcAppElement;
  ycAppEntryPoint?: YcAppEntryPoint;
  ycAppSurface?: YcAppSurface;
  userId?: string | null;
}) {
  return {
    claimTokenHash: args.claimTokenHash ?? null,
    eventFamily: YC_APP_EVENT_FAMILY,
    formatId: "yc",
    isLoggedIn: args.isLoggedIn ?? false,
    projectId: args.projectId ?? null,
    userId: args.userId ?? null,
    ycAppElement: args.ycAppElement ?? null,
    ycAppEntryPoint: args.ycAppEntryPoint ?? null,
    ycAppSurface: args.ycAppSurface ?? "public",
  };
}
